<script lang="ts">
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getXertAm5, type am5Root } from '@/Util/XertAm5';
  import { shortDuration } from './format';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // power_curve of a report: modeled power against duration from the signature at the start and at the end.
  export let curve: { durations: number[]; start: number[]; end: number[]; marker_seconds: number | null } | null = null;
  export let endLabel: string | null = null;

  const chartId = `power-curve-${Math.random().toString(36).slice(2)}`;
  let element: HTMLElement;
  let root: am5Root | undefined;
  const TICKS = [1, 15, 60, 300, 1200, 3600];

  $: if (element && curve) rebuild(curve, endLabel);
  function rebuild(...args: unknown[]) {
    void args;
    root?.dispose();
    build();
  }

  function build() {
    const { am5, am5xy, MyTheme } = getXertAm5();
    am5.addLicense('AM5C87942260');
    root = am5.Root.new(element);
    root.setThemes([MyTheme.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { paddingLeft: 0, paddingRight: 12, layout: root.verticalLayout }));

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.labels.template.set('forceHidden', true);
    xRenderer.grid.template.set('forceHidden', true);
    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: xRenderer, logarithmic: true, min: 1, max: 3600, strictMinMax: true }));
    for (const t of TICKS) {
      const item = xAxis.makeDataItem({ value: t });
      xAxis.createAxisRange(item);
      item.get('grid')?.setAll({ strokeOpacity: 0.08, visible: true, forceHidden: false });
      item.get('label')?.setAll({ text: shortDuration(t), visible: true, forceHidden: false, fontSize: 11, fill: am5.color('#6B7280') });
    }
    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
    yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, min: 0 }));
    yAxis.ghostLabel.set('minWidth', 36);

    const rows = curve!.durations.map((d, i) => ({ d, start: curve!.start[i], end: curve!.end[i] }));
    const line = (name: string, field: string, color: string, dashed: boolean) => {
      const s = chart.series.push(am5xy.SmoothedXLineSeries.new(root!, { name, xAxis, yAxis, valueXField: 'd', valueYField: field, stroke: am5.color(color),
        tooltip: am5.Tooltip.new(root!, { labelText: `{valueY.formatNumber('#')} W` }) }));
      s.strokes.template.setAll({ strokeWidth: 2.5, strokeDasharray: dashed ? [5, 4] : [] });
      s.data.setAll(rows);
      return s;
    };
    line($_('report.overview.start'), 'start', '#9CA3AF', true);
    const end = line(endLabel ?? $_('report.overview.end'), 'end', '#2563EB', false);

    const m = curve!.marker_seconds;
    if (m) {
      const at = rows.find((r) => r.d === m);
      if (at) {
        end.bullets.push((_r: any, _s: any, item: any) => {
          const isMarker = item.dataContext?.d === m;
          return am5.Bullet.new(root!, { sprite: am5.Circle.new(root!, { radius: isMarker ? 6 : 0, fill: am5.color('#2563EB'), stroke: am5.color('#fff'), strokeWidth: 2 }) });
        });
        const item = xAxis.makeDataItem({ value: m });
        xAxis.createAxisRange(item);
        item.get('grid')?.setAll({ stroke: am5.color('#6B7280'), strokeOpacity: 0.6, strokeDasharray: [4, 3], visible: true, forceHidden: false });
      }
    }
    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 6 }));
    legend.labels.template.setAll({ fontSize: 12 });
    legend.data.setAll(chart.series.values);
  }
  onDestroy(() => root?.dispose());
</script>

<Am5Loader>
  <div id={chartId} bind:this={element} class="w-full h-56" />
</Am5Loader>
